import { walkPath, describeNode } from './jxpathSchema';

export const PDS_XPATH_LANGUAGE_ID = 'pdsxpath';

/** @type {boolean} */
let registered = false;

/**
 * Replace string literals with spaces so brackets/parens inside strings are ignored.
 */
export function maskStringLiterals(text) {
  let out = '';
  let q = null;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '\\' && q !== '`') {
        out += ' ';
        i++;
        if (i < text.length) out += ' ';
        continue;
      }
      out += ' ';
      if (c === q) q = null;
      continue;
    }
    if (c === '"' || c === "'") {
      q = c;
      out += ' ';
      continue;
    }
    out += c;
  }
  return out;
}

/**
 * Index of the `[` that opens the innermost bracket pair containing the cursor
 * (cursor = end of masked string). Returns -1 if not inside `[...]`.
 */
function innermostOpenBracketIndex(masked) {
  let depth = 0;
  for (let i = masked.length - 1; i >= 0; i--) {
    const c = masked[i];
    if (c === ']') depth++;
    else if (c === '[') {
      if (depth === 0) return i;
      depth--;
    }
  }
  return -1;
}

/**
 * Parse `/a/b/c` style steps; each segment may include `[predicate]` (predicate skipped for names).
 * Steps returned as { name } for walkPath.
 */
export function parseLeadingPathSteps(pathFragment) {
  const steps = [];
  let rest = pathFragment
    .replace(/^\s*xpath:\s*/i, '')
    .replace(/\/{2,}/g, '/')
    .replace(/^\/+/, '');
  while (rest.length) {
    const m = rest.match(/^([^/[.\s]+)/);
    if (!m) break;
    const name = m[1];
    steps.push({ name });
    rest = rest.slice(name.length);
    if (rest.startsWith('[')) {
      let depth = 1;
      let j = 1;
      while (j < rest.length && depth > 0) {
        if (rest[j] === '[') depth++;
        else if (rest[j] === ']') depth--;
        j++;
      }
      rest = rest.slice(j);
    }
    if (rest.startsWith('/')) rest = rest.slice(1);
    else break;
  }
  return steps;
}

/**
 * After stripping strings, find parenthesis depth at end of string.
 */
function parenDepthAtEnd(masked) {
  let d = 0;
  for (let i = 0; i < masked.length; i++) {
    const c = masked[i];
    if (c === '(') d++;
    else if (c === ')') d--;
  }
  return d;
}

/**
 * Slice of masked / original text from the start of the current "path-ish" segment
 * (after last comma at paren depth 0, or whole thing).
 */
function currentArgFragment(text, masked) {
  let depth = 0;
  let lastComma = -1;
  for (let i = 0; i < masked.length; i++) {
    const c = masked[i];
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === ',' && depth === 0) lastComma = i;
  }
  const start = lastComma + 1;
  return { fragment: text.slice(start).trimStart(), maskedFrag: masked.slice(start).trimStart() };
}

/**
 * @param {string} textBeforeCursor
 * @returns {{ kind: string, prefix?: string, partial?: string, steps?: { name: string }[], predicateInner?: string }}
 */
export function analyzeContext(textBeforeCursor) {
  const trimmed = textBeforeCursor.trimEnd();
  const masked = maskStringLiterals(trimmed);

  // fn:xpath:local:op: namespace completion
  const nsFull = trimmed.match(/\b(fn|xpath|local|op):\s*$/i);
  if (nsFull) {
    return { kind: 'fn-namespace', prefix: nsFull[1].toLowerCase(), partial: '' };
  }
  const nsPartial = trimmed.match(/\b(fn|xpath|local|op):([A-Za-z_][\w-]*)$/i);
  if (nsPartial) {
    return {
      kind: 'fn-namespace',
      prefix: nsPartial[1].toLowerCase(),
      partial: nsPartial[2],
    };
  }

  const bracketIdx = innermostOpenBracketIndex(masked);
  const insidePredicate = bracketIdx >= 0;

  if (insidePredicate) {
    const innerMasked = masked.slice(bracketIdx + 1);
    const innerRaw = trimmed.slice(bracketIdx + 1);
    const cmp = innerMasked.match(/^([\w.-]+)\s*[=!<>]+\s*/);
    if (cmp) {
      return {
        kind: 'predicate-value',
        steps: [],
        predicateInner: innerRaw,
      };
    }
    const predNameMatch = innerMasked.match(/^([\w.-]*)/);
    const partial = predNameMatch ? predNameMatch[1] : '';
    const pathBeforePred = trimmed.slice(0, bracketIdx);
    const steps = parseLeadingPathSteps(pathBeforePred.replace(/^\s*xpath:\s*/i, ''));
    return {
      kind: 'predicate-name',
      steps,
      partial,
    };
  }

  // Path step: use fragment after last top-level comma for fn args
  const { fragment, maskedFrag } = currentArgFragment(trimmed, masked);
  let pathPart = fragment.replace(/^\s*xpath:\s*/i, '');

  if (parenDepthAtEnd(maskedFrag) > 0) {
    const idx = maskedFrag.lastIndexOf('(');
    if (idx >= 0) {
      pathPart = fragment.slice(idx + 1).trim().replace(/^\s*xpath:\s*/i, '');
    }
  }

  const lastSlash = Math.max(pathPart.lastIndexOf('/'), pathPart.lastIndexOf('//'));
  let partial = lastSlash >= 0 ? pathPart.slice(lastSlash + 1) : pathPart;
  partial = partial.replace(/\[[^\]]*$/, '');
  partial = partial.replace(/^[^\w.-]+/, '');

  const basePath = lastSlash >= 0 ? pathPart.slice(0, lastSlash + 1) : '';
  const steps = parseLeadingPathSteps(basePath.replace(/^\s*xpath:\s*/i, ''));

  return {
    kind: 'path-step',
    steps,
    partial: partial.replace(/\/.*/, ''),
  };
}

/** ns: 'bare' = no prefix; 'fn' = XPath fn: namespace */
export const PDS_XPATH_FUNCTIONS = [
  { label: 'string-length', ns: 'bare', insert: 'string-length(${1:string})', doc: 'Returns string length.' },
  { label: 'expr', ns: 'bare', insert: 'expr(${1:expression})', doc: 'Evaluate sub-expression.' },
  { label: 'if', ns: 'bare', insert: 'if(${1:condition}, ${2:then}, ${3:else})', doc: 'Conditional.' },
  { label: 'month-from-date', ns: 'bare', insert: 'month-from-date(${1:date})', doc: 'Month from date.' },
  { label: 'count', ns: 'bare', insert: 'count(${1:nodes})', doc: 'Count nodes.' },
  { label: 'sum', ns: 'bare', insert: 'sum(${1:nodes})', doc: 'Sum values.' },
  { label: 'not', ns: 'bare', insert: 'not(${1:boolean})', doc: 'Logical not.' },
  { label: 'concat', ns: 'fn', insert: 'fn:concat(${1:a}, ${2:b})', doc: 'Concatenate strings.' },
  { label: 'substring', ns: 'fn', insert: 'fn:substring(${1:str}, ${2:start}, ${3:length})', doc: 'Substring.' },
  { label: 'contains', ns: 'fn', insert: 'fn:contains(${1:str}, ${2:substr})', doc: 'Contains substring.' },
  { label: 'starts-with', ns: 'fn', insert: 'fn:starts-with(${1:str}, ${2:substr})', doc: 'Starts with.' },
  { label: 'upper-case', ns: 'fn', insert: 'fn:upper-case(${1:str})', doc: 'Upper case.' },
  { label: 'lower-case', ns: 'fn', insert: 'fn:lower-case(${1:str})', doc: 'Lower case.' },
  { label: 'normalize-space', ns: 'fn', insert: 'fn:normalize-space(${1:str})', doc: 'Normalize whitespace.' },
  { label: 'string', ns: 'fn', insert: 'fn:string(${1:value})', doc: 'String value.' },
  { label: 'number', ns: 'fn', insert: 'fn:number(${1:value})', doc: 'Numeric value.' },
  { label: 'boolean', ns: 'fn', insert: 'fn:boolean(${1:value})', doc: 'Boolean value.' },
];

function suggestFunctions(ctx, range, monaco) {
  const prefix = ctx.prefix || '';
  const partial = (ctx.partial || '').toLowerCase();
  const suggestions = [];

  if (prefix === 'xpath') {
    suggestions.push({
      label: 'xpath:/…',
      kind: monaco.languages.CompletionItemKind.Keyword,
      insertText: 'xpath:/${1:path}',
      insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      documentation: { value: 'JXPath expression with xpath: prefix.' },
      sortText: '0000',
      range,
    });
    return { suggestions };
  }

  if (prefix === 'local' || prefix === 'op') {
    return { suggestions: [] };
  }

  if (prefix !== 'fn') {
    return { suggestions: [] };
  }

  PDS_XPATH_FUNCTIONS.forEach((fn, index) => {
    if (fn.ns !== 'fn') return;
    const filterKey = fn.label.toLowerCase();
    if (partial && !filterKey.startsWith(partial)) return;

    suggestions.push({
      label: `fn:${fn.label}`,
      kind: monaco.languages.CompletionItemKind.Function,
      insertText: fn.insert,
      insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      documentation: fn.doc ? { value: fn.doc } : undefined,
      sortText: String(index).padStart(4, '0'),
      range,
    });
  });

  return { suggestions };
}

function suggestChildren(ctx, tree, range, monaco) {
  const node = walkPath(tree, ctx.steps || []);
  if (!node || node.kind !== 'object') {
    return { suggestions: [] };
  }
  const partial = (ctx.partial || '').toLowerCase();
  const suggestions = [];
  Object.keys(node.children).forEach((key, index) => {
    if (partial && !key.toLowerCase().startsWith(partial)) return;
    const child = node.children[key];
    suggestions.push({
      label: key,
      kind: monaco.languages.CompletionItemKind.Property,
      insertText: key,
      detail: describeNode(child),
      sortText: String(index).padStart(4, '0'),
      range,
    });
  });
  return { suggestions };
}

/**
 * @param {import('monaco-editor').editor.ITextModel} model - May hold __pdsTree
 * @param {*} monaco
 */
export function registerPdsXpath(monaco) {
  if (registered) return;
  registered = true;

  monaco.languages.register({ id: PDS_XPATH_LANGUAGE_ID });

  monaco.languages.setMonarchTokensProvider(PDS_XPATH_LANGUAGE_ID, {
    tokenizer: {
      root: [
        [/\b(xpath|fn|local|op):/, 'keyword'],
        [
          /\b(and|or|not|div|mod|eq|ne|lt|le|gt|ge|if|then|else|return|for|in)\b/,
          'keyword.operator',
        ],
        [/\$[A-Za-z_][\w-]*/, 'variable'],
        [/'[^']*'|"[^"]*"/, 'string'],
        [/\b\d+(\.\d+)?\b/, 'number'],
        [/\/\/|\//, 'delimiter'],
        [/[\[\]\(\),]/, 'delimiter.bracket'],
        [/[A-Za-z_][\w-]*/, 'identifier'],
      ],
    },
  });

  monaco.languages.setLanguageConfiguration(PDS_XPATH_LANGUAGE_ID, {
    brackets: [
      ['(', ')'],
      ['[', ']'],
    ],
    autoClosingPairs: [
      { open: '(', close: ')' },
      { open: '[', close: ']' },
      { open: "'", close: "'" },
      { open: '"', close: '"' },
    ],
    surroundingPairs: [
      { open: '(', close: ')' },
      { open: '[', close: ']' },
      { open: "'", close: "'" },
      { open: '"', close: '"' },
    ],
  });

  monaco.languages.registerCompletionItemProvider(PDS_XPATH_LANGUAGE_ID, {
    triggerCharacters: ['/', '[', ':', '(', ',', "'", '"'],
    provideCompletionItems(model, position) {
      const tree = model.__pdsTree;
      const text = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const ctx = analyzeContext(text);

      if (ctx.kind === 'predicate-value') {
        return { suggestions: [] };
      }

      if (ctx.kind === 'fn-namespace') {
        const nsMatch = text.match(/(fn|xpath|local|op):([A-Za-z_][\w-]*)?$/i);
        const nsRange = nsMatch
          ? {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: position.column - nsMatch[0].length,
              endColumn: position.column,
            }
          : range;
        return suggestFunctions(ctx, nsRange, monaco);
      }

      if (ctx.kind === 'path-step' || ctx.kind === 'predicate-name') {
        return suggestChildren(ctx, tree, range, monaco);
      }

      return { suggestions: [] };
    },
  });
}
